import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, NoteBlock } from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'
import { exactMinorBig, isCurrencyCode } from '@/lib/money/money'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Pricing & Billing › Fee events (`pr-events`) and Waivers & promotions
 * (`pr-waivers`), from `GET /v1/admin/fee-events` and `/fee-waivers`.
 *
 * # NOTHING IN THE FEE EVENTS LIST HAS BEEN COLLECTED
 *
 * This is the single most important thing either screen says, and it is the
 * backend's own words: `collected` is false on **every row by construction**.
 * These are fees that were COMPUTED and RECORDED. No code path in this build
 * debits a fee at the institution — a fee is a movement between a customer and
 * the operator, so law 2 requires it to be executed at the institution first
 * and posted from the resulting provider event, and that work is unbuilt.
 *
 * So a total over these rows is **what would be owed** and never what moved.
 * Reading it as income reports money the journal does not contain, which is why
 * this screen renders no total at all: a figure with a disclaimer beside it is a
 * figure somebody will quote without the disclaimer. The absence is the control.
 *
 * # The window is not defaulted, and the screen says which one it read
 *
 * `from` and `to` are optional and omitting them lists the most recent page
 * across all time rather than a period somebody chose. "A fee total whose
 * window was invented is a figure an operator cannot interpret and cannot
 * reproduce" — so this console omits them and states that it did, rather than
 * picking a month and letting the screen imply somebody asked for it.
 *
 * # Both screens are read-only, and that is a property of the seam
 *
 * There is no record, no amend, no collect and no refund. `fee_events` is
 * append-only and a correction is a new event, never an edit (law 5); a waiver
 * is withdrawn by setting an end and never by deleting a row. The interface the
 * routes hang off has one method and it is a list, so neither screen could
 * offer a control even if one were drawn.
 *
 * # Withdrawn and expired promotions are listed, not filtered out
 *
 * A fee waived in January has to stay explainable in July, and the fee event
 * that cites a promotion records its code AND its version precisely so the
 * promotion reconstructs after it stopped running. "A list showing only live
 * promotions would answer 'what took this off my fee' with nothing, for exactly
 * the fees somebody is asking about."
 */

export const FEE_EVENTS_PATH = '/fee-events'
export const FEE_WAIVERS_PATH = '/fee-waivers'

const EVENTS_SOURCE = 'postgres · fee_events (GET /v1/admin/fee-events)'
const WAIVERS_SOURCE = 'postgres · fee_waivers (GET /v1/admin/fee-waivers)'
const AUTHORITY = 'operational'

// ---------------------------------------------------------------------------
// Fee events
// ---------------------------------------------------------------------------

const EVENT_COLS: readonly Column[] = [
  ['Occurred', 'm', '150px'],
  ['Customer', 'm', '100px'],
  ['Kind', 'm', 'minmax(170px,1.2fr)'],
  ['Method', 'm', '120px'],
  ['Schedule', 'm', '140px'],
  ['Base', 'a', '140px'],
  ['Fee computed', 'a', '150px'],
  ['Capped by', 'x', '130px'],
  ['Promotion', 'x', 'minmax(150px,1fr)'],
  ['Rung', 'x', '150px'],
  ['Collected', 'c', '130px'],
]

interface FeeEvent {
  readonly occurredAt: string
  readonly customerId: number | null
  readonly kind: string
  readonly method: string
  readonly code: string
  readonly version: number | null
  readonly base: Money | null
  readonly fee: Money | null
  readonly cappedBy: string
  readonly waiverCode: string
  readonly waiverVersion: number | null
  readonly scheduled: Money | null
  readonly scope: string
  readonly segment: string
  readonly precedence: number | null
  /** Whatever the backend said, read as three states. Never assumed. */
  readonly collected: boolean | null
}

interface Money {
  readonly minor: number
  readonly currency: string
}

/**
 * A `moneyView`, or `null`.
 *
 * `amount_minor` arrives as a quoted string or a bare int and goes through
 * `exactMinorBig` — the only thing allowed to turn either into a number here
 * (law 9). A value that is not an exact integer, or that is beyond what an
 * amount cell can hold exactly, is **absent** rather than rounded: a fee
 * rendered a cent out is worse than a fee not rendered, because nothing
 * downstream could tell.
 */
function readMoney(raw: unknown): Money | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const currency = typeof o['currency'] === 'string' ? o['currency'] : ''
  if (!isCurrencyCode(currency)) return null
  const minor = exactMinorBig(o['amount_minor'])
  if (minor === null) return null
  if (minor > BigInt(Number.MAX_SAFE_INTEGER) || minor < -BigInt(Number.MAX_SAFE_INTEGER)) {
    return null
  }
  return { minor: Number(minor), currency }
}

function readEvent(raw: unknown): FeeEvent | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const waiver = typeof o['waiver'] === 'object' && o['waiver'] !== null
    ? (o['waiver'] as Record<string, unknown>)
    : null
  return {
    occurredAt: str(o['occurred_at']),
    customerId: int(o['customer_id']),
    kind: str(o['kind']),
    method: str(o['method']),
    code: str(o['price_list_code']),
    version: int(o['price_list_version']),
    base: readMoney(o['base']),
    fee: readMoney(o['fee']),
    cappedBy: str(o['capped_by']),
    waiverCode: waiver === null ? '' : str(waiver['code']),
    waiverVersion: waiver === null ? null : int(waiver['version']),
    scheduled: readMoney(o['scheduled_fee']),
    scope: str(o['resolved_scope']),
    segment: str(o['resolved_segment']),
    // A POINTER all the way out to JSON, and absent off the segment rung.
    // Coalescing it to 0 would rank every customer-rung and default-rung event
    // as the strongest cohort there is.
    precedence: int(o['resolved_precedence']),
    collected: typeof o['collected'] === 'boolean' ? o['collected'] : null,
  }
}

function eventRow(e: FeeEvent): readonly Cell[] {
  return [
    e.occurredAt === '' ? '— not stated' : day(e.occurredAt),
    e.customerId === null ? '—' : String(e.customerId),
    e.kind === '' ? '— not stated' : e.kind,
    e.method === '' ? 'no method' : e.method,
    e.code === '' ? '— not stated' : `${e.code}${e.version === null ? '' : ` v${String(e.version)}`}`,
    // Absent when the fee had none — never zero, "because zero is a real base
    // and a false zero is a claim about the world".
    e.base === null ? 'no base' : ([e.base.minor, e.base.currency] as Cell),
    e.fee === null ? '— unreadable' : ([e.fee.minor, e.fee.currency] as Cell),
    e.cappedBy === '' ? 'not capped' : e.cappedBy,
    promotionCell(e),
    rungCell(e),
    collectedCell(e),
  ]
}

/**
 * What a promotion took off, if one did.
 *
 * `scheduled_fee` is what the schedule said before the promotion, and it is
 * never a copy of the fee: "the schedule said this and a promotion took that
 * off" and "the schedule said this" are different facts. Where a waiver is
 * cited its version is shown too, because that is what makes it reconstructable
 * after it stopped running.
 */
function promotionCell(e: FeeEvent): Cell {
  if (e.waiverCode === '') return 'none'
  const cited = `${e.waiverCode}${e.waiverVersion === null ? '' : ` v${String(e.waiverVersion)}`}`
  return e.scheduled === null ? cited : `${cited} · was ${String(e.scheduled.minor)} ${e.scheduled.currency}`
}

/**
 * Which rung resolved this customer's schedule.
 *
 * "Somebody put them on it" and "it is the default" are different answers, and
 * an operator asked why a customer paid this needs to know which before they go
 * looking for an assignment row that may not exist.
 */
function rungCell(e: FeeEvent): Cell {
  if (e.scope === '') return '— not stated'
  if (e.scope !== 'segment') return e.scope
  const seg = e.segment === '' ? 'unnamed cohort' : e.segment
  return e.precedence === null ? `segment · ${seg}` : `segment · ${seg} (p${String(e.precedence)})`
}

/**
 * `collected`, which is false on every row and is rendered as the fact it is.
 *
 * Tone `a` rather than `n`: a fee computed and never charged is a standing
 * condition somebody should notice, not a neutral column. It is never rendered
 * as a tick, and an unread value says so rather than defaulting to either.
 */
function collectedCell(e: FeeEvent): ChipCell {
  if (e.collected === null) return ['— not stated', 'r']
  return e.collected ? ['collected', 'g'] : ['computed, not charged', 'a']
}

const NOT_COLLECTED: NoteBlock = {
  t: 'note',
  tone: 'a',
  kicker: 'What this list is',
  title: 'Nothing here has been charged to anybody',
  body: 'Every row is a fee that was computed and recorded. No code path in this build debits a fee at the institution — a fee moves money between a customer and us, so it has to be executed at the institution first and posted from the provider event that comes back, and that work does not exist yet. A total over these rows is what WOULD be owed, never what moved, which is why this screen shows no total: a figure with a caveat beside it is a figure somebody quotes without the caveat.',
  ref: 'collected is false on every row by construction · law 2 · collection is a separate seam',
}

const NO_WINDOW: NoteBlock = {
  t: 'note',
  tone: 'n',
  kicker: 'Window',
  title: 'This is the most recent page across all time, not a period anybody chose',
  body: 'The endpoint takes an optional half-open window and this console sends none, so what you are reading is the latest page of everything rather than a month. That is deliberate: a fee total whose window was invented is a figure an operator cannot interpret and cannot reproduce, and a screen that quietly picked one would look like it was answering a question somebody asked.',
  ref: 'from and to are optional and are not defaulted',
}

const EVENTS_READ_ONLY: NoteBlock = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Law 5',
  title: 'There is no amend, no collect and no refund here',
  body: 'Fee events are append-only: a correction is a new event and never an edit, which is what keeps a fee charged in January explainable in July. Collection is not a console act at all — it would go through the institution. The seam this screen reads has one method and it is a list, so this is a property of the types rather than a promise somebody has to keep.',
  ref: 'no route on this surface records, amends, collects or refunds a fee',
}

export function composeFeeEvents(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['fee_events']) ? (payload['fee_events'] as readonly unknown[]) : []
  const rows: FeeEvent[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readEvent(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const blocks: Block[] = [{
    t: 'table',
    title: 'Fee events',
    src: 'ledger',
    facets: false,
    cols: EVENT_COLS,
    rows: rows.map(eventRow),
    footer: `${String(rows.length)} event(s) on this page${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'Each row names the schedule and version it was computed under, so the fee reconstructs from the price list even after that version stops governing.',
  }]

  blocks.push(NOT_COLLECTED, NO_WINDOW, EVENTS_READ_ONLY)
  if (unreadable > 0) blocks.push(unreadableNote(unreadable, 'fee event'))
  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'g',
      kicker: 'Fee events',
      title: 'No fee has been computed yet',
      body: 'The endpoint answered with an empty list. This is a counted zero rather than a failed read — no fee event has been recorded on this cell across all time, because no window was applied to narrow it.',
      ref: 'an empty list, not an empty window',
    })
  }

  const missing = anyMissing(rows, unreadable)
  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: EVENTS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) }
    : { kind: 'partial', source: EVENTS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing }
  return { blocks, provenance }
}

function anyMissing(rows: readonly FeeEvent[], unreadable: number): readonly string[] {
  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  if (rows.some((r) => r.fee === null)) missing.push('the computed fee on at least one row')
  if (rows.some((r) => r.collected === null)) missing.push('whether at least one fee was collected')
  // Always. The window is not a failure to read — it is a decision — but a
  // screen listing "the most recent page" is not listing a period, and a
  // provenance that claimed otherwise would be the screen's only untrue part.
  missing.push('any window — this is the most recent page, not a chosen period')
  return missing
}

export const loadFeeEvents: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(FEE_EVENTS_PATH)
  } catch (error) {
    return unreadable(error, 'The fee-events endpoint', EVENTS_SOURCE)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The fee-events endpoint returned a shape this console does not know.', null, EVENTS_SOURCE)
  }
  return composeFeeEvents(payload as Record<string, unknown>, new Date())
}

// ---------------------------------------------------------------------------
// Waivers and promotions
// ---------------------------------------------------------------------------

const WAIVER_COLS: readonly Column[] = [
  ['Promotion', 'm', '150px'],
  ['Applies to', 'x', 'minmax(170px,1.2fr)'],
  ['Fee it covers', 'm', 'minmax(160px,1.1fr)'],
  ['Takes off', 'a', '150px'],
  ['Runs', 'm', 'minmax(180px,1.2fr)'],
  ['Ended early', 'm', '140px'],
  ['Granting now', 'c', '150px'],
]

interface Waiver {
  readonly code: string
  readonly version: number | null
  readonly scope: string
  readonly customerId: number | null
  readonly segmentKey: string
  readonly precedence: number | null
  readonly itemType: string
  readonly method: string
  readonly currency: string
  readonly form: string
  readonly percentOffBps: number | null
  readonly amountOff: Money | null
  readonly from: string
  readonly to: string
  readonly withdrawnAt: string
  readonly ends: string
  readonly live: boolean | null
}

function readWaiver(raw: unknown): Waiver | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  return {
    code: str(o['code']),
    version: int(o['version']),
    scope: str(o['scope']),
    customerId: int(o['customer_id']),
    segmentKey: str(o['segment_key']),
    precedence: int(o['precedence']),
    itemType: str(o['item_type']),
    method: str(o['method']),
    currency: str(o['currency']),
    form: str(o['form']),
    percentOffBps: int(o['percent_off_bps']),
    amountOff: readMoney(o['amount_off']),
    from: str(o['effective_from']),
    to: str(o['effective_to']),
    withdrawnAt: str(o['withdrawn_at']),
    ends: str(o['ends']),
    live: typeof o['live'] === 'boolean' ? o['live'] : null,
  }
}

function waiverRow(w: Waiver): readonly Cell[] {
  return [
    w.code === '' ? '— not stated' : `${w.code}${w.version === null ? '' : ` v${String(w.version)}`}`,
    appliesTo(w),
    covers(w),
    takesOff(w),
    `${w.from === '' ? '—' : day(w.from)} → ${w.to === '' ? 'open' : day(w.to)}`,
    // `ends` is the instant it ACTUALLY stops granting — its stated end, or its
    // withdrawal if that came first. Served rather than derived here, because
    // two derivations of "when did this stop" is one promotion the console and
    // the fee path can disagree about.
    w.withdrawnAt === '' ? 'ran to its end' : `withdrawn ${day(w.withdrawnAt)}`,
    grantingCell(w),
  ]
}

function appliesTo(w: Waiver): Cell {
  if (w.scope === 'customer') {
    return w.customerId === null ? 'one customer — id not stated' : `customer ${String(w.customerId)}`
  }
  if (w.scope === 'segment') {
    const seg = w.segmentKey === '' ? 'unnamed cohort' : w.segmentKey
    return w.precedence === null ? `segment · ${seg}` : `segment · ${seg} (p${String(w.precedence)})`
  }
  return w.scope === '' ? '— not stated' : w.scope
}

function covers(w: Waiver): Cell {
  const kind = w.itemType === '' ? '— not stated' : w.itemType
  const method = w.method === '' ? '' : ` · ${w.method}`
  const cur = w.currency === '' ? '' : ` · ${w.currency}`
  return `${kind}${method}${cur}`
}

/**
 * What the promotion removes, in its own form.
 *
 * A percentage is basis points and stays basis points — converting it to a
 * decimal percentage here would put a float on a money path for the sake of
 * looking familiar. An amount is minor units beside its currency (laws 8, 9).
 */
function takesOff(w: Waiver): Cell {
  if (w.amountOff !== null) return [w.amountOff.minor, w.amountOff.currency]
  if (w.percentOffBps !== null && w.percentOffBps !== 0) return `${String(w.percentOffBps)} bps off`
  if (w.form === '') return '— not stated'
  return `${w.form} — amount not on this read`
}

/**
 * `live`, as the backend computed it, at the instant it named.
 *
 * Never recomputed here from the dates. `live` comes from the same predicate
 * the fee path resolves with, and a console that derived its own would be a
 * second implementation that disagrees about a boundary eventually — on the one
 * screen somebody consults to find out why a fee was or was not waived.
 */
function grantingCell(w: Waiver): ChipCell {
  if (w.live === null) return ['— not stated', 'r']
  return w.live ? ['granting', 'g'] : ['not granting', 'n']
}

function historyNote(asOf: string): NoteBlock {
  return {
    t: 'note',
    tone: 'n',
    kicker: 'History',
    title: 'Withdrawn and expired promotions are listed here, not filtered out',
    body: `Withdrawing a promotion sets an end; it never deletes a row. A fee waived in January has to stay explainable in July, and a list showing only what is running today would answer "what took this off my fee" with nothing — for exactly the fees somebody is asking about. Granting now was computed by the backend at ${asOf === '' ? 'an instant it did not state' : asOf}, using the same predicate the fee path resolves with.`,
    ref: 'a fee event cites a promotion by code AND version, so it reconstructs after it stops running',
  }
}

const WAIVERS_READ_ONLY: NoteBlock = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Law 5',
  title: 'Promotions are not created, edited or deleted from here',
  body: 'This surface lists them and nothing more. A promotion is withdrawn by setting an end rather than by removing a row, so the fee it already covered stays explainable — and no route on this surface grants one, changes one or takes one away.',
  ref: 'the seam this screen reads has one method and it is a list',
}

export function composeFeeWaivers(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['waivers']) ? (payload['waivers'] as readonly unknown[]) : []
  const rows: Waiver[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readWaiver(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }
  const asOf = str(payload['as_of'])

  const blocks: Block[] = [{
    t: 'table',
    title: 'Waivers and promotions',
    src: 'ledger',
    facets: false,
    cols: WAIVER_COLS,
    rows: rows.map(waiverRow),
    footer: `${String(rows.length)} promotion(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`} · ${String(rows.filter((w) => w.live === true).length)} granting now`,
    foot2:
      'A promotion is addressed by code AND version, because the version is what a fee event cites and what makes an old waiver reconstructable.',
  }]

  blocks.push(historyNote(asOf), WAIVERS_READ_ONLY)
  if (unreadable > 0) blocks.push(unreadableNote(unreadable, 'promotion'))
  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'g',
      kicker: 'Promotions',
      title: 'No promotion has ever been recorded',
      body: 'The endpoint answered with an empty list, and this list is not filtered — withdrawn and expired promotions would appear here if there were any. So this is a statement about every promotion that has ever existed, not only about what is running today.',
      ref: 'a counted zero over an unfiltered list',
    })
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  if (asOf === '') missing.push('the instant "granting now" was computed against')
  if (rows.some((w) => w.live === null)) missing.push('whether at least one promotion is granting')

  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: WAIVERS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) }
    : { kind: 'partial', source: WAIVERS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing }
  return { blocks, provenance }
}

export const loadFeeWaivers: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(FEE_WAIVERS_PATH)
  } catch (error) {
    return unreadable(error, 'The fee-waivers endpoint', WAIVERS_SOURCE)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The fee-waivers endpoint returned a shape this console does not know.', null, WAIVERS_SOURCE)
  }
  return composeFeeWaivers(payload as Record<string, unknown>, new Date())
}

// ---------------------------------------------------------------------------

function unreadable(error: unknown, what: string, source: string): ScreenData {
  if (error instanceof ApiError) {
    return unreadableScreen(
      `${what} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
      error.decisionRef,
      source,
    )
  }
  if (error instanceof DOMException && error.name === 'AbortError') throw error
  return unreadableScreen('The backend could not be reached.', null, source)
}

function unreadableScreen(reason: string, decisionRef: string | null, source: string): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'This screen could not be read',
      body: `${reason} Nothing below is known, so nothing is drawn.`,
      // Law 13: the reference travels as itself, quotable without the prose.
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source, reason },
  }
}

function unreadableNote(count: number, what: string): NoteBlock {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Partial read',
    title: `${String(count)} ${what} row(s) could not be read`,
    body: 'They are counted rather than folded into the rows that did parse, because a list shorter than the record with nothing saying so is the failure worth reporting.',
    ref: 'the count is the finding',
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

/** An integer, or `null`. Never a coerced zero — absence is a real state here. */
function int(v: unknown): number | null {
  return exactInteger(v)
}

/** The day part of an RFC3339 stamp, or the whole thing if it has no `T`. */
function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
